import { useEffect, useState } from "react";
import { isStaleBuild, localBuildId } from "../../lib/build-version.ts";
import { fetchServerBuildId } from "../../lib/runtime-config.ts";

/**
 * Как редко вкладка спрашивает сервер о версии. Полчаса: выкатки происходят
 * несколько раз в неделю, а не в минуту, и опрашивать чаще значит платить
 * запросом за то, что почти никогда не меняется.
 */
const CHECK_EVERY_MS = 30 * 60 * 1000;

/**
 * Полоса «вышла новая версия».
 *
 * **Зачем.** Вкладку кабинета не закрывают неделями, а весь интерфейс, тексты и
 * записи «Что нового» живут в бандле. Без этой полосы человек не узнает о
 * новом, пока сам не перезагрузит страницу, а повода у него нет. Разбор
 * механизма и отвергнутого «убивать сессию через час» в `lib/build-version.ts`.
 *
 * ⚠️ **Страница НЕ перезагружается сама ни при каких условиях.** Ни по таймеру,
 * ни при возвращении фокуса, ни через `skipWaiting` у service worker, когда он
 * появится. Перезагрузка посреди занятия это ровно та поломка, против которой
 * построен продукт: несохранённый ответ ученика, оборванный WebSocket, чёрный
 * экран у обоих. Решает человек, и решает нажатием.
 *
 * ⚠️ **В комнате урока полосы нет, и держится это МЕСТОМ, а не условием.**
 * Компонент живёт внутри каркаса кабинета (`Shell`), а `/lesson/:id`,
 * `/archive/:id` и `/join/:code` стоят ВНЕ него (`App.tsx`). Значит и проверка
 * версии там не запускается вовсе. Перенос этого компонента выше маршрутов
 * «чтобы видели все» ломает главное требование: не трогать человека, пока идёт
 * занятие.
 *
 * ⚠️ **Проверка идёт по возвращении к вкладке, а не по таймеру.** Опрос по
 * `setInterval` работает и в фоне: десяток открытых вкладок превращались бы в
 * десяток запросов в час у человека, который на них не смотрит. Возвращение
 * фокуса это ровно тот момент, когда полоса ему может пригодиться.
 */
export function BuildFreshnessBar() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const local = localBuildId();
    // Версия неизвестна (дев, локальная сборка без EDU_BUILD_ID): сравнивать не
    // с чем, и ни одного запроса отсюда не уходит
    if (local === undefined) return;

    let alive = true;
    let lastCheck = 0;

    const check = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastCheck < CHECK_EVERY_MS) return;
      lastCheck = now;
      void fetchServerBuildId().then((server) => {
        // ⚠️ Только в одну сторону: узнав однажды, что вкладка устарела, полосу
        // не убираем. Обратно свежей она стать не может, а мигающая полоса
        // выглядит как поломка
        if (alive && isStaleBuild(local, server)) setStale(true);
      });
    };

    check();
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  if (!stale) return null;

  return (
    <div className="newsbar newsbar--muted" role="status">
      <span className="newsbar__text">
        Вышла новая версия. Обновите страницу, когда будет удобно.
      </span>
      {/*
        Кнопка без акцента, в отличие от полосы про заданное: там новость с
        одним продолжением, а здесь справка, которой можно не пользоваться
        сейчас. Кнопки «закрыть» нет по тому же правилу, что у полосы домашки:
        закрывать извещение, которое вернётся, это лишнее нажатие с нулевым
        эффектом.
      */}
      <button type="button" className="btn" onClick={() => location.reload()}>
        Обновить
      </button>
    </div>
  );
}
